import { assets, CenteredPage, PrimaryButton, SecondaryButton } from '@cmg/common';
import { Box, Checkbox, Divider, FormControlLabel, Stack, Typography } from '@cmg/design-system';
import React from 'react';

import type { ConsentInfo } from '../../../types/domain/consent/consentInfo';
import ClientName from './components/ClientName';
import ExchangeArrows from './components/ExchangeArrows';
import Mark from './components/Mark';
import PublisherMark, { MARK_INK } from './components/PublisherMark';
import RedirectTarget from './components/RedirectTarget';
import Scope from './components/Scope';
import { actsOnBehalfOfUser } from './ConsentScreen.model';
import { onDark, secondaryButtonOnDark } from './consentSurface';

export type ConsentScreenProps = {
  consentInfo: ConsentInfo;
  /** The decision in flight, if any. Both buttons lock so a second click cannot burn the code. */
  pendingDecision: 'grant' | 'deny' | null;
  onGrant: (rememberConsent: boolean) => void;
  onDeny: () => void;
};

export const ConsentScreen: React.FC<ConsentScreenProps> = ({
  consentInfo,
  pendingDecision,
  onGrant,
  onDeny,
}) => {
  const [rememberConsent, setRememberConsent] = React.useState(false);
  const statusRef = React.useRef<HTMLParagraphElement>(null);

  // The shared button sets `tabIndex={-1}` on itself once it is loading, so submitting from the
  // keyboard drops focus to the body with nothing announced. Move it onto the status line instead,
  // which is where the only news is.
  React.useEffect(() => {
    if (pendingDecision !== null) {
      // `preventScroll`, because the node it lands on is a clipped 1px one: without it the browser
      // scrolls to bring that node into view and the panel jumps under the user mid-decision.
      statusRef.current?.focus({ preventScroll: true });
    }
  }, [pendingDecision]);

  // Escape cancels, as it does in any dialog. Refusing is the safe answer here — it returns the
  // user to the app with `error=access_denied` — so it is the one that gets the shortcut.
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && pendingDecision === null) {
        onDeny();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [pendingDecision, onDeny]);

  return (
    // The shell's background only — no CMG logo header, and no marketing footer: "Get in touch ·
    // Visit cmgx.io" are two ways off a security decision, and the only two 11.5px targets here.
    <CenteredPage hideHeader hideFooter>
      {/* A definite width, not a max-width: `SContent` is a flex item at `min-width: auto`, so the
          unbreakable origin line would otherwise floor it at that line's own width and push the
          panel off both edges of a phone. A definite width also gives `text-overflow` on the origin
          something to overflow, which is what makes it ellipsise instead of just being clipped.
          CenteredPage's column has no gutter of its own, hence the 16px either side. */}
      <Box
        component="main"
        sx={{ ...onDark.panel, p: 3, mt: 4, mx: 'auto', width: 'min(400px, calc(100vw - 32px))' }}
      >
        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
          <Mark>
            <PublisherMark host={consentInfo.publisherHost} />
          </Mark>
          <ExchangeArrows />
          <Mark>
            {/* The artwork runs to the edges of its own viewBox, so the box width *is* the ink. */}
            <Box component="img" src={assets.logoNoTextSvg} alt="CMG" sx={{ width: MARK_INK }} />
          </Mark>
        </Stack>

        {/* Clamped: the name is the app's own string, and an unbounded one pushed the decision
            itself off the first screen. The full name stays in the DOM, so it is still what a
            screen reader announces. No `title` here — a host-language title outranks name-from-
            content in the accessible name computation, which would rename the heading to the
            client's string alone. */}
        <Typography
          variant="h1"
          component="h1"
          textAlign="center"
          sx={{
            ...onDark.secondaryText,
            minWidth: 0,
            mt: 2.5,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 3,
            overflow: 'hidden',
          }}
        >
          Authorize <ClientName name={consentInfo.clientName} />
        </Typography>

        {/* Completes the heading's sentence. Says the one thing the scope list cannot: the app acts
            *as* the user, so what it does will look like what they did. Withheld when only identity
            scopes are requested, where it would not be true. */}
        {actsOnBehalfOfUser(consentInfo.scopes) && (
          <Typography variant="body1" textAlign="center" sx={{ ...onDark.mutedText, mt: 0.5 }}>
            to act on your behalf in your CMG account.
          </Typography>
        )}

        <Divider sx={{ ...onDark.divider, my: 3 }} />

        {/* A lead-in, not a title: sentence case against the theme's `capitalize` on h1-h4, and a
            step below the scope names it introduces. Every scope's display name is a verb phrase
            that completes it. */}
        <Typography
          variant="body1"
          component="h2"
          sx={{ ...onDark.secondaryText, textTransform: 'none', mb: 1.5 }}
        >
          This app needs your permission to:
        </Typography>
        {consentInfo.scopes.length === 0 ? (
          <Typography variant="h3" sx={{ textTransform: 'none' }}>
            Sign you in, and nothing else. It cannot read or change your data.
          </Typography>
        ) : (
          <Stack
            component="ul"
            // `list-style: none` strips list semantics in Safari/VoiceOver, and the number of
            // permissions being granted is exactly what a screen reader should hear.
            role="list"
            gap={2}
            sx={{ listStyle: 'none', m: 0, p: 0 }}
          >
            {consentInfo.scopes.map(scope => (
              <Scope key={scope.name} scope={scope} />
            ))}
          </Stack>
        )}

        {consentInfo.canRemember && (
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberConsent}
                  onChange={() => setRememberConsent(current => !current)}
                  // The theme's `primary` is the on-light blue and all but disappears here; checked
                  // has to be the dark-background one, and the empty box has to be visible unlit.
                  sx={{
                    ...onDark.secondaryText,
                    '&.Mui-checked': { color: 'primaryDarkBackground.main' },
                  }}
                />
              }
              label="Remember my decision"
            />
          </Box>
        )}

        <RedirectTarget redirectUri={consentInfo.redirectUri} />

        <Typography
          ref={statusRef}
          tabIndex={-1}
          role="status"
          aria-live="polite"
          variant="body2"
          component="p"
          // Not shown: the button's own label already says this, and printing it twice reads as a
          // stutter. This exists to announce the change and to catch the focus the button drops.
          sx={{
            position: 'absolute',
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
          }}
        >
          {pendingDecision === 'grant' && 'Authorizing…'}
          {pendingDecision === 'deny' && 'Cancelling…'}
        </Typography>

        {/* The login screen's own submit button, so consent and sign-in read as one flow. The gap
            is what stops the redirect target above from reading as this button's own caption. */}
        <Box sx={{ mt: 3 }}>
          <PrimaryButton
            variant="high-contrast"
            size="large"
            fullWidth
            loading={pendingDecision === 'grant'}
            loadingLabel="Authorizing…"
            disabled={pendingDecision !== null}
            onClick={() => onGrant(rememberConsent)}
          >
            Authorize as {consentInfo.userDisplayName}
          </PrimaryButton>
        </Box>
        <Box sx={{ mt: 1.5, ...secondaryButtonOnDark }}>
          <SecondaryButton
            size="large"
            fullWidth
            loading={pendingDecision === 'deny'}
            loadingLabel="Cancelling…"
            disabled={pendingDecision !== null}
            onClick={onDeny}
          >
            Cancel
          </SecondaryButton>
        </Box>
      </Box>
    </CenteredPage>
  );
};

export default ConsentScreen;
